function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { Linking, Platform } from "react-native";
import semverCompare from "semver-compare";
import { graphql } from "@oui/lib/src/graphql/tada";
import { ConfirmationModal } from "./ConfirmationModal";
import { getNativeBuildVersionAsSemver, manifest } from "../constants";
import { usePersistedState } from "../hooks/usePersistedState";
import { useI18n } from "../lib/i18n";
/*
 * minimumAppVersion query returns version minimums based on other requesting client so it is OK
 * to compare to the build's native version vs using this as a feature flag when different app clients
 * may be on inconsistent versions
 */ function nativeVersionAtLeast(version) {
    var nativeBuildVersion = getNativeBuildVersionAsSemver();
    return semverCompare(nativeBuildVersion !== null && nativeBuildVersion !== void 0 ? nativeBuildVersion : '99.99.99', version) >= 0;
}
export var MinimumAppVersionQuery = graphql("\n  query MinimumAppVersion {\n    minimumAppVersion {\n      recommendedMinimumVersion\n      requiredMinimumVersion\n    }\n  }\n");
/**
 * Checks the API to see if the current app version is old enough to recommend
 * or require an upgrade to the user. If so, displays the appropriate UI /
 * prompt.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-49}
 */ export function MinimumAppVersion(param) {
    var children = param.children;
    var _usePersistedState = _sliced_to_array(usePersistedState('minOuiAppVersion', null), 1), minOuiAppVersion = _usePersistedState[0];
    var _useState = _sliced_to_array(useState(true), 2), isVisible = _useState[0], setIsVisibile = _useState[1];
    var $t = useI18n().$t;
    var requestContext = {};
    if (minOuiAppVersion) {
        requestContext = {
            headers: {
                'X-Mock-Oui-Min-Version': JSON.stringify(minOuiAppVersion)
            }
        };
    }
    var data = useQuery(MinimumAppVersionQuery, {
        context: requestContext
    }).data;
    var versionDetails = useMemo(function() {
        var currentAppVersion = getNativeBuildVersionAsSemver();
        var _ref = (data === null || data === void 0 ? void 0 : data.minimumAppVersion) || {
            recommendedMinimumVersion: '0.0.0',
            requiredMinimumVersion: '0.0.0'
        }, recommendedMinimumVersion = _ref.recommendedMinimumVersion, requiredMinimumVersion = _ref.requiredMinimumVersion;
        var isAtLeastMinimumRecomendedVersion = nativeVersionAtLeast(recommendedMinimumVersion);
        var isAtLeastMinimumRequiredVersion = nativeVersionAtLeast(requiredMinimumVersion);
        var requiresAppUpgrade = !isAtLeastMinimumRecomendedVersion || !isAtLeastMinimumRequiredVersion;
        return {
            recommendedMinimumVersion: recommendedMinimumVersion,
            requiredMinimumVersion: requiredMinimumVersion,
            isAtLeastMinimumRecomendedVersion: isAtLeastMinimumRecomendedVersion,
            isAtLeastMinimumRequiredVersion: isAtLeastMinimumRequiredVersion,
            requiresAppUpgrade: !!currentAppVersion && requiresAppUpgrade,
            canBeDismissed: isAtLeastMinimumRequiredVersion
        };
    }, [
        data
    ]);
    var dismissAppUpgradeModal = function() {
        setIsVisibile(false);
    };
    var onConfirm = function() {
        if (Platform.OS === 'ios') {
            void Linking.openURL("https://itunes.apple.com/us/app/aviva/id1480869629");
        } else if (Platform.OS === 'android') {
            void Linking.openURL("https://play.google.com/store/apps/details?id=com.ouitherapeutics.aviva");
        } else {
            global.window.location.reload();
        }
    };
    var onDismiss = function() {
        dismissAppUpgradeModal();
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            versionDetails.requiresAppUpgrade && isVisible ? /*#__PURE__*/ _jsx(ConfirmationModal, {
                visible: true,
                title: $t({
                    id: 'MinimumAppVersion_title',
                    defaultMessage: 'Update Available'
                }),
                description: !versionDetails.canBeDismissed ? $t({
                    id: 'MinimumAppVersion_optionalUpgrade',
                    defaultMessage: "There's a newer version of {appName} that's available for download. Please update to continue using {appName}."
                }, {
                    appName: manifest.name
                }) : $t({
                    id: 'MinimumAppVersion_requiredUpgrade',
                    defaultMessage: "There's a newer version of {appName} that's available for download. We recommend updating to ensure you have the best experience possible."
                }, {
                    appName: manifest.name
                }),
                onCancel: versionDetails.canBeDismissed ? onDismiss : null,
                cancelText: $t({
                    id: 'MinimumAppVersion_dismissButton',
                    defaultMessage: 'Not now'
                }),
                cancelTestID: "MinimumAppVersion_dismissButton",
                onConfirm: onConfirm,
                confirmText: $t({
                    id: 'MinimumAppVersion_upgradeButton',
                    defaultMessage: 'Update'
                }),
                confirmTestID: "MinimumAppVersion_upgradeButton"
            }) : null,
            children
        ]
    });
}
